import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";

import { Card, CardHeader } from "@/components/ui/Card";
import type { TicketRow } from "@/lib/tickets-api";

/** A short list of tickets (favorites, recently viewed) with a line explaining an empty list. */
export function TicketShortcutsCard({
  id,
  title,
  empty,
  queryKey,
  queryFn,
}: {
  id: string;
  title: string;
  empty: ReactNode;
  queryKey: readonly unknown[];
  queryFn: () => Promise<TicketRow[]>;
}) {
  const { t } = useTranslation();
  const tickets = useQuery({ queryKey, queryFn });
  const items = tickets.data?.slice(0, 10) ?? [];
  const numbers = items.map((item) => item.number);
  return (
    <Card aria-labelledby={id}>
      <CardHeader title={<span id={id}>{title}</span>} />
      {tickets.data && items.length === 0 ? (
        <p className="px-5 py-4 text-sm text-ink-muted">{empty}</p>
      ) : (
        <ul className="divide-y divide-line">
          {items.map((ticket) => (
            <li key={ticket.id} className="px-5 py-2.5">
              <Link
                to={`/tickets/${encodeURIComponent(ticket.number)}`}
                state={{ from: "/", numbers }}
                className="block text-sm break-words text-ink underline-offset-4 hover:text-primary hover:underline"
              >
                <span className="text-ink-muted tabular-nums">#{ticket.number}</span>{" "}
                {ticket.title || t("tickets.untitled")}
              </Link>
              <span className="text-xs text-ink-subtle">{ticket.queue}</span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
